import React, { useEffect, useMemo } from 'react';
import { Form, FormikProvider, useFormik } from 'formik';
import * as Yup from 'yup';
import { Alert, Autocomplete, Box, LinearProgress, Stack, TextField } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { CancelButton, SubmitButton, UpdateButton } from '../../../components/mui/Buttons/Buttons';
import useHandleApiResponse from '../../../Hooks/useHandleApiResponse';
import {
  createIntervalCommunities,
  updateIntervalCommunities,
  getIntervalCommunityPreData,
} from '../../../redux/features/intervalCommunitySlice';

//A WHATSAPP GROUP INVITE IS ALWAYS AN HTTP(S) URL. THE SAME LOOSE CHECK THE API USES.
const LINK_PATTERN = /^https?:\/\/\S+$/i;
const LINK_ERROR = 'Enter a valid link starting with http:// or https://';

// Add / Edit a single interval community. `row` present → edit mode.
//
// The class decides the shape of the form:
//   class 1 .. 10  → one link per syllabus (State Board and CBSE)
//   every other    → a single community link
// The flag comes from the API (`requires_syllabus` on each class), so the rule is
// never duplicated here.
const IntervalCommunityAddEdit = ({ row, onClose }) => {
  const dispatch = useDispatch();
  const { mutating, preData } = useSelector((state) => state.intervalCommunity);
  const handleApiResponse = useHandleApiResponse();

  useEffect(() => {
    dispatch(getIntervalCommunityPreData());
  }, [dispatch]);

  const classes = preData?.classes || [];
  const syllabuses = preData?.syllabuses || [];

  // Existing links keyed by syllabus id, so edit mode can prefill either shape.
  const existingLinks = useMemo(() => {
    const map = {};
    (row?.links || []).forEach((link) => {
      if (link.syllabus_id !== null && link.syllabus_id !== undefined) {
        map[link.syllabus_id] = link.whatsapp_link ?? '';
      }
    });
    return map;
  }, [row]);

  const initialValues = useMemo(
    () => ({
      title: row?.title ?? '',
      class_id: row?.class_id ?? '',
      // used when the class does NOT need the syllabus split
      community_link:
        row?.community_link ??
        (row?.links || []).find((link) => link.syllabus_id === null)?.whatsapp_link ??
        '',
      // used when it does — keyed by syllabus id
      syllabus_links: existingLinks,
    }),
    [row, existingLinks],
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: Yup.lazy((values) => {
      const selected = classes.find((item) => item.id === values.class_id);
      const requiresSyllabus = Boolean(selected?.requires_syllabus);

      return Yup.object({
        title: Yup.string().trim().required('Title is required'),
        class_id: Yup.number().required('Class is required'),
        community_link: requiresSyllabus
          ? Yup.string().nullable()
          : Yup.string().trim().matches(LINK_PATTERN, LINK_ERROR).required('Community link is required'),
        syllabus_links: requiresSyllabus
          ? Yup.object(
              syllabuses.reduce((shape, syllabus) => {
                shape[syllabus.id] = Yup.string()
                  .trim()
                  .matches(LINK_PATTERN, LINK_ERROR)
                  .required(`${syllabus.name} link is required`);
                return shape;
              }, {}),
            )
          : Yup.object().nullable(),
      });
    }),
    onSubmit: async (values) => {
      const selected = classes.find((item) => item.id === values.class_id);
      const requiresSyllabus = Boolean(selected?.requires_syllabus);

      const links = requiresSyllabus
        ? syllabuses.map((syllabus) => ({
            syllabus_id: syllabus.id,
            whatsapp_link: (values.syllabus_links?.[syllabus.id] || '').trim(),
          }))
        : [{ syllabus_id: null, whatsapp_link: values.community_link.trim() }];

      const payload = {
        title: values.title.trim(),
        class_id: Number(values.class_id),
        links,
      };

      let res;
      if (row) {
        // UPDATE — community_id goes in the body (coerced to number inside the slice).
        res = await dispatch(updateIntervalCommunities({ community_id: row.community_id, ...payload }));
      } else {
        // CREATE — slice wraps this into { data: [ ... ] }.
        res = await dispatch(createIntervalCommunities(payload));
      }

      handleApiResponse(res, {
        onSuccess: () => {
          formik.resetForm();
          if (onClose) onClose(true); // refetch the table
        },
      });
    },
  });

  const selectedClass = classes.find((item) => item.id === formik.values.class_id) || null;
  const requiresSyllabus = Boolean(selectedClass?.requires_syllabus);

  const handleCancel = () => {
    formik.resetForm();
    if (onClose) onClose(false);
  };

  // Switching between the two shapes clears the fields that no longer apply, so a
  // stale value can never be submitted for the wrong class.
  const handleClassChange = (event, option) => {
    const nextClass = option?.id ?? '';
    const nextRequiresSyllabus = Boolean(option?.requires_syllabus);

    formik.setFieldValue('class_id', nextClass);
    if (nextRequiresSyllabus !== requiresSyllabus) {
      formik.setFieldValue('community_link', '');
      formik.setFieldValue('syllabus_links', {});
      formik.setFieldTouched('community_link', false);
      formik.setFieldTouched('syllabus_links', false);
    }
  };

  const syllabusFieldError = (syllabusId) =>
    formik.touched.syllabus_links?.[syllabusId] && formik.errors.syllabus_links?.[syllabusId];

  return (
    <FormikProvider value={formik}>
      <Form onSubmit={formik.handleSubmit}>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            fullWidth
            id="title"
            name="title"
            label="Title"
            value={formik.values.title}
            onChange={formik.handleChange}
            error={formik.touched.title && Boolean(formik.errors.title)}
            helperText={formik.touched.title && formik.errors.title}
          />

          <Autocomplete
            options={classes}
            loading={preData?.loading}
            getOptionLabel={(option) => option?.name || ''}
            isOptionEqualToValue={(option, val) => option.id === val.id}
            value={selectedClass}
            onChange={handleClassChange}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                autoComplete="off"
                label="Class"
                error={formik.touched.class_id && Boolean(formik.errors.class_id)}
                helperText={formik.touched.class_id && formik.errors.class_id}
              />
            )}
          />

          {/* Class 1..10 → one link per syllabus. Anything else → a single link. */}
          {requiresSyllabus ? (
            <>
              <Alert severity="info" sx={{ py: 0.5 }}>
                Class {selectedClass?.name} needs a separate community link for each syllabus.
              </Alert>

              {syllabuses.map((syllabus) => (
                <TextField
                  key={syllabus.id}
                  fullWidth
                  id={`syllabus_links.${syllabus.id}`}
                  name={`syllabus_links.${syllabus.id}`}
                  label={`${syllabus.name} Community WhatsApp Link`}
                  placeholder="https://chat.whatsapp.com/..."
                  value={formik.values.syllabus_links?.[syllabus.id] || ''}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={Boolean(syllabusFieldError(syllabus.id))}
                  helperText={syllabusFieldError(syllabus.id) || ''}
                />
              ))}
            </>
          ) : (
            <TextField
              fullWidth
              id="community_link"
              name="community_link"
              label="Community WhatsApp Link"
              placeholder="https://chat.whatsapp.com/..."
              value={formik.values.community_link}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={!formik.values.class_id}
              error={formik.touched.community_link && Boolean(formik.errors.community_link)}
              helperText={
                (formik.touched.community_link && formik.errors.community_link) ||
                (!formik.values.class_id ? 'Select a class first' : '')
              }
            />
          )}

          {mutating && (
            <Box sx={{ width: '100%' }}>
              <LinearProgress />
            </Box>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Stack direction="row" spacing={1}>
              <CancelButton action={handleCancel}>Cancel</CancelButton>
              {row ? (
                <UpdateButton disabled={mutating} type="submit">
                  {mutating ? 'Updating...' : 'Update'}
                </UpdateButton>
              ) : (
                <SubmitButton disabled={mutating} type="submit">
                  {mutating ? 'Submitting...' : 'Submit'}
                </SubmitButton>
              )}
            </Stack>
          </Box>
        </Stack>
      </Form>
    </FormikProvider>
  );
};

export default IntervalCommunityAddEdit;
